import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse } from '@core/models/common/api-response.model';
import { ApiResponseHandler } from '@core/handlers/api-response.handler';
import { QuestionSet } from '@core/models/question-set.model';
import { TestSubmission } from '@core/models/test-submission.model';
import { NextItem } from '../../models/common/next-item.model';
import { SubmissionReview } from '@core/models/submission-review.model';
import { ActivityNextItem } from '../../models/common/activity-next-item.model';
import { APP_CONFIG } from '@core/config/app-config.token';
import { AppConfig } from '@core/config/app-config.model';

@Injectable({
  providedIn: 'root',
})
export class QuestionSubmissionService {
  private conferenceUrl!: string;
  private submissionUrl!: string;
  private submissionSkipUrl!: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig
  ) {
    this.conferenceUrl = `${this.cfg.serviceApiUrl}/conferences`;
    this.submissionUrl = `${this.cfg.serviceApiUrl}/submission`;
    this.submissionSkipUrl = `${this.cfg.serviceApiUrl}/skip/submission`;
  }

  getQuestionSet(conferenceUuid: string, questionSetUuid: string): Observable<QuestionSet> {
    return this.http
      .get<
        ApiResponse<QuestionSet>
      >(`${this.conferenceUrl}/${conferenceUuid}/questionSets/${questionSetUuid}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<QuestionSet>(response) as QuestionSet),
        catchError(ApiResponseHandler.handleError)
      );
  }

  submitTest(data: TestSubmission): Observable<ApiResponse<NextItem>> {
    return this.http
      .post<ApiResponse<NextItem>>(this.submissionUrl, data)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  submitSkipTest(data: TestSubmission): Observable<ApiResponse<NextItem>> {
    return this.http
      .post<ApiResponse<NextItem>>(this.submissionSkipUrl, data)
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getSubmissionReview(
    courseUuid: string,
    questionSetUuid: string,
    submissionId: number
  ): Observable<SubmissionReview> {
    const url = `${this.cfg.serviceApiUrl}/courses/${courseUuid}/questionSets/${questionSetUuid}/review/${submissionId}`;

    return this.http.get<ApiResponse<SubmissionReview>>(url).pipe(
      map((res) => {
        const data = ApiResponseHandler.extractData<SubmissionReview>(res);
        return Array.isArray(data) ? data[0] : data;
      }),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getNextItem(courseUuid: string): Observable<ActivityNextItem> {
    const url = `${this.cfg.serviceApiUrl}/get-next-item/course/${courseUuid}`;

    return this.http.get<ApiResponse<ActivityNextItem>>(url).pipe(
      map((res) => {
        const data = ApiResponseHandler.extractData<ActivityNextItem>(res);
        return Array.isArray(data) ? data[0] : data;
      }),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getConferenceNextItem(conferenceUuid: string): Observable<ActivityNextItem> {
    const url = `${this.cfg.serviceApiUrl}/get-next-item/conference/${conferenceUuid}`;

    return this.http.get<ApiResponse<ActivityNextItem>>(url).pipe(
      map((res) => {
        const data = ApiResponseHandler.extractData<ActivityNextItem>(res);
        return Array.isArray(data) ? data[0] : data;
      }),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getPushLiveNextItem(pushLiveUuid: string): Observable<ActivityNextItem> {
    const url = `${this.cfg.serviceApiUrl}/get-next-item/push-live/${pushLiveUuid}`;
    return this.http.get<ApiResponse<ActivityNextItem>>(url).pipe(
      map((res) => {
        const data = ApiResponseHandler.extractData<ActivityNextItem>(res);
        return Array.isArray(data) ? data[0] : data;
      }),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getConferenceSubmissionReview(
    liveUuid: string,
    questionSetUuid: string,
    submissionId: number
  ): Observable<SubmissionReview> {
    const url = `${this.cfg.serviceApiUrl}/conferences/${liveUuid}/questionSets/${questionSetUuid}/review/${submissionId}`;

    return this.http.get<ApiResponse<SubmissionReview>>(url).pipe(
      map((res) => {
        const data = ApiResponseHandler.extractData<SubmissionReview>(res);
        return Array.isArray(data) ? data[0] : data;
      }),
      catchError(ApiResponseHandler.handleError)
    );
  }
}
